import { ReactNode, useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, CircularProgress } from '@mui/material';
import { InteractionStatus } from '@azure/msal-browser';
import { DEV_AUTH_BYPASS } from './devAuth';
import { loginRequest } from './msalConfig';
import { SignInRequired } from './SignInRequired';
import { isApiAuthRejected, subscribeApiAuth } from './authState';

export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  // MSAL can consider us signed in while the API refuses the token — a blocked sign-in domain
  // looks exactly like that. Treat it as "not in the app" rather than letting every screen fail.
  const [apiRejected, setApiRejected] = useState(isApiAuthRejected);
  useEffect(() => {
    const unsubscribe = subscribeApiAuth(setApiRejected);
    return () => { unsubscribe(); };
  }, []);

  // Redirect at most once. If sign-in fails or the user cancels, a second automatic redirect
  // would trap them in a loop with no way to choose a different account.
  const [redirectAttempted, setRedirectAttempted] = useState(false);

  useEffect(() => {
    if (DEV_AUTH_BYPASS || apiRejected) return;
    if (!isAuthenticated && inProgress === InteractionStatus.None && !redirectAttempted) {
      setRedirectAttempted(true);
      void instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance, redirectAttempted, apiRejected]);

  if (DEV_AUTH_BYPASS) return <>{children}</>;
  if (apiRejected) return <SignInRequired reason="notAllowed" />;
  if (isAuthenticated) return <>{children}</>;

  // Sign-in was attempted and we are still not authenticated: offer an explicit way in rather
  // than spinning forever.
  if (redirectAttempted && inProgress === InteractionStatus.None) {
    return <SignInRequired reason="signedOut" />;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );
}
